import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { MessageService } from 'primeng/api';
import { TopbarService } from 'src/app/core/topbar/topbar.service';
import { Filter, FilterOperation, Filter as JsonApiFilter, OrFilter } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { Case } from '../../cases/_types/case';
import { CasesService } from '../../cases/_services/cases.service';
import { CompaniesService } from '../../companies/_services/companies.service';
import { Company } from '../../companies/_types/company';
import { Contract } from '../../companies/_types/contract';
import { ContractsService } from '../../companies/_services/contracts.service';
import { LoginService } from '../../login/login.service';
import { CreateDataProtectionRequestPayload, DataProtectionRequestsService } from '../_services/data-protection-requests.service';

type PersonalDataKey = 'name' | 'legalName' | 'address' | 'phone1' | 'phone2' | 'phone3' | 'fax' | 'email' | 'vatNumber' | 'iban' | 'contactPerson' | 'policy';
type AttachmentKey = 'photos' | 'documents';
type EventKey = 'calls';
type RetentionKey = 'budgets' | 'deliveryNotes' | 'invoices';

interface PersonalDataOption {
  key: PersonalDataKey;
  label: string;
}

interface AttachmentOption {
  key: AttachmentKey;
  label: string;
}

interface EventOption {
  key: EventKey;
  label: string;
}

interface RetentionOption {
  key: RetentionKey;
  label: string;
}

@Component({
    selector: 'app-data-protection-detail',
    templateUrl: './data-protection-detail.component.html',
    styleUrls: ['./data-protection-detail.component.scss'],
    standalone: false
})
export class DataProtectionDetailComponent implements OnInit {
  @Input()
  public embeddedInModal: boolean = false;

  public allowWrite: boolean = false;

  public personalDataOptions: PersonalDataOption[] = [
    { key: 'name', label: 'Nombre' },
    { key: 'legalName', label: 'Razón Social' },
    { key: 'address', label: 'Dirección' },
    { key: 'phone1', label: 'Teléfono 1 y descripción' },
    { key: 'phone2', label: 'Teléfono 2 y descripción' },
    { key: 'phone3', label: 'Teléfono 3 y descripción' },
    { key: 'fax', label: 'Fax' },
    { key: 'email', label: 'Email' },
    { key: 'vatNumber', label: 'NIF' },
    { key: 'iban', label: 'Número IBAN' },
    { key: 'contactPerson', label: 'Persona de contacto' },
    { key: 'policy', label: 'Póliza' }
  ];

  public attachmentOptions: AttachmentOption[] = [
    { key: 'photos', label: 'Fotos' },
    { key: 'documents', label: 'Documentos' }
  ];

  public eventOptions: EventOption[] = [
    { key: 'calls', label: 'Llamadas' }
  ];

  public retentionOptions: RetentionOption[] = [
    { key: 'budgets', label: 'Presupuestos' },
    { key: 'deliveryNotes', label: 'Albaranes' },
    { key: 'invoices', label: 'Facturas' }
  ];

  public companies: Company[] = [];
  public contracts: Contract[] = [];
  public cases: Case[] = [];
  public selectedCases: Case[] = [];
  public totalRecords: number = 0;
  public loading: boolean = false;
  public deleting: boolean = false;
  public hasSearched: boolean = false;
  public showDeleteDialog: boolean = false;

  public deleteRequest = this.createDefaultDeleteRequest();

  private lastGridEvent: any = null;
  private allMatchingCaseIds: string[] = [];
  private deselectedCaseIds: Set<string> = new Set<string>();
  private syncingSelection: boolean = false;

  public filters = {
    dateFrom: this.getPreviousQuarterStart(),
    dateTo: this.getPreviousQuarterEnd(),
    companyId: null as string | null,
    contractId: null as string | null,
    name: '',
    phone: '',
    email: ''
  };

  constructor(
    private topbarService: TopbarService,
    private messageService: MessageService,
    private casesService: CasesService,
    private companiesService: CompaniesService,
    private contractsService: ContractsService,
    private dataProtectionRequestsService: DataProtectionRequestsService,
    private loginService: LoginService,
    private router: Router
  ) {
  }

  async ngOnInit(): Promise<void> {
    if (!this.loginService.hasPermission('PROTECTION_DATA.ACCESS') || !this.loginService.hasPermission('PROTECTION_DATA.WRITE')) {
      this.router.navigate(['/home']);
      return;
    }

    this.allowWrite = true;

    if (!this.embeddedInModal) {
      this.topbarService.setTitle('Protección de datos');
    }
    await this.loadCompanies();
  }

  public async search(): Promise<void> {
    if (!this.validateFilters()) {
      return;
    }

    this.hasSearched = true;
    this.cases = [];
    this.selectedCases = [];
    this.totalRecords = 0;
    this.allMatchingCaseIds = [];
    this.deselectedCaseIds = new Set<string>();

    if (this.lastGridEvent) {
      this.lastGridEvent.first = 0;
      await this.loadData(this.lastGridEvent);
    } else {
      await this.loadData({ first: 0, rows: 25, sortField: 'caseDate', sortOrder: 1 });
    }
  }

  public async loadData($event: any): Promise<void> {
    this.lastGridEvent = $event;

    if (!this.hasSearched) {
      this.cases = [];
      this.selectedCases = [];
      this.totalRecords = 0;
      return;
    }

    this.loading = true;

    try {
      const params: IParamsCollection = {
        sort: [($event.sortOrder > 0 ? '' : '-') + ($event.sortField || 'caseDate')],
        page: { number: $event.first + 1, size: $event.rows },
        include: ['caseState', 'client'],
        filter: FilterBuilder.fromObject(this.buildSearchFilters()).build()
      };

      const response = await firstValueFrom(this.casesService.all(params));
      this.cases = response.data;
      this.totalRecords = response.meta['total'];
      this.syncPageSelection();

      if (this.totalRecords > 0 && this.allMatchingCaseIds.length === 0) {
        await this.loadAllMatchingCaseIds();
        this.syncPageSelection();
      }

      if (this.totalRecords === 0) {
        this.allMatchingCaseIds = [];
        this.deselectedCaseIds.clear();
        this.messageService.add({ closable: true, severity: 'info', detail: 'No se han encontrado expedientes con los filtros indicados' });
      }
    } catch (error: any) {
      this.messageService.add({ closable: true, severity: 'error', detail: error?.error?.title || 'Ha ocurrido un error al buscar los expedientes' });
    } finally {
      this.loading = false;
    }
  }

  public onSelectionChange(selection: Case[]): void {
    if (this.syncingSelection) {
      return;
    }

    this.selectedCases = selection;
    const selectedIds = new Set(selection.map(caseItem => caseItem.id));

    for (const caseItem of this.cases) {
      if (!caseItem.id) {
        continue;
      }

      if (selectedIds.has(caseItem.id)) {
        this.deselectedCaseIds.delete(caseItem.id);
      } else {
        this.deselectedCaseIds.add(caseItem.id);
      }
    }
  }

  public async toggleAllCasesSelection(checked: boolean): Promise<void> {
    if (!this.hasSearched || this.totalRecords === 0) {
      return;
    }

    if (this.allMatchingCaseIds.length === 0) {
      await this.loadAllMatchingCaseIds();
    }

    this.deselectedCaseIds = checked ? new Set<string>() : new Set<string>(this.allMatchingCaseIds);
    this.syncPageSelection();
  }

  public areAllCasesSelected(): boolean {
    return this.allMatchingCaseIds.length > 0 && this.getSelectedRecordsCount() === this.allMatchingCaseIds.length;
  }

  public isCasesSelectionIndeterminate(): boolean {
    const selectedCount = this.getSelectedRecordsCount();
    return selectedCount > 0 && selectedCount < this.allMatchingCaseIds.length;
  }

  public getSelectedRecordsCount(): number {
    return Math.max(this.allMatchingCaseIds.length - this.deselectedCaseIds.size, 0);
  }

  public confirmDeleteSelected(): void {
    if (!this.allowWrite) {
      return;
    }

    if (this.getSelectedRecordsCount() === 0) {
      this.messageService.add({ closable: true, severity: 'error', detail: 'Debe seleccionar al menos un expediente' });
      return;
    }

    this.deleteRequest = this.createDefaultDeleteRequest();
    this.showDeleteDialog = true;
  }

  public closeDeleteDialog(): void {
    this.showDeleteDialog = false;
  }

  public getPersonalDataValue(key: PersonalDataKey): boolean {
    return this.deleteRequest.personalData[key];
  }

  public setPersonalDataValue(key: PersonalDataKey, value: boolean): void {
    this.deleteRequest.personalData[key] = value;
  }

  public getAttachmentValue(key: AttachmentKey): boolean {
    return this.deleteRequest.attachments[key];
  }

  public setAttachmentValue(key: AttachmentKey, value: boolean): void {
    this.deleteRequest.attachments[key] = value;
  }

  public getEventValue(key: EventKey): boolean {
    return this.deleteRequest.events[key];
  }

  public setEventValue(key: EventKey, value: boolean): void {
    this.deleteRequest.events[key] = value;
  }

  public getRetentionValue(key: RetentionKey): boolean {
    return this.deleteRequest.retention[key];
  }

  public setRetentionValue(key: RetentionKey, value: boolean): void {
    this.deleteRequest.retention[key] = value;
  }

  public async submitDeleteDialog(): Promise<void> {
    if (!this.allowWrite) {
      return;
    }

    if (!this.filters.contractId) {
      this.messageService.add({ closable: true, severity: 'error', detail: 'El contrato es obligatorio' });
      return;
    }

    if (!this.deleteRequest.confirmPermanentDeletion) {
      this.messageService.add({ closable: true, severity: 'error', detail: 'Debe confirmar que es consciente de la eliminación permanente de los datos' });
      return;
    }

    this.showDeleteDialog = false;
    await this.createDeleteRequest();
  }

  public clearFilters(): void {
    this.filters = {
      dateFrom: this.getPreviousQuarterStart(),
      dateTo: this.getPreviousQuarterEnd(),
      companyId: null,
      contractId: null,
      name: '',
      phone: '',
      email: ''
    };
    this.contracts = [];
    this.resetSearchState();
  }

  public onContractChange(): void {
    this.resetSearchState();
  }

  public async onCompanyChange(): Promise<void> {
    this.filters.contractId = null;
    this.contracts = [];
    this.resetSearchState();

    if (!this.filters.companyId) {
      return;
    }

    await this.loadContracts(this.filters.companyId);
  }

  private resetSearchState(): void {
    this.hasSearched = false;
    this.cases = [];
    this.selectedCases = [];
    this.totalRecords = 0;
    this.allMatchingCaseIds = [];
    this.deselectedCaseIds = new Set<string>();
  }

  private async loadCompanies(): Promise<void> {
    const params: IParamsCollection = {
      sort: ['name'],
      page: { number: 1, size: 1000 }
    };

    const response = await firstValueFrom(this.companiesService.all(params));
    this.companies = response.data;
  }

  private async loadContracts(companyId: string): Promise<void> {
    const params: IParamsCollection = {
      sort: ['name'],
      page: { number: 1, size: 1000 },
      filter: FilterBuilder.fromObject({
        companyId: new Filter(FilterOperation.equals, 'companyId', '', companyId)
      }).build()
    };

    const response = await firstValueFrom(this.contractsService.all(params));
    this.contracts = response.data;
  }

  private validateFilters(): boolean {
    if (!this.filters.dateFrom) {
      this.messageService.add({ closable: true, severity: 'error', detail: 'La fecha desde es obligatoria' });
      return false;
    }

    if (!this.filters.dateTo) {
      this.messageService.add({ closable: true, severity: 'error', detail: 'La fecha hasta es obligatoria' });
      return false;
    }

    if (!this.filters.companyId) {
      this.messageService.add({ closable: true, severity: 'error', detail: 'La compañía es obligatoria' });
      return false;
    }

    if (!this.filters.contractId) {
      this.messageService.add({ closable: true, severity: 'error', detail: 'El contrato es obligatorio' });
      return false;
    }

    const dateFrom = this.getStartOfDay(this.filters.dateFrom);
    const dateTo = this.getEndOfDay(this.filters.dateTo);
    if (dateFrom > dateTo) {
      this.messageService.add({ closable: true, severity: 'error', detail: 'La fecha desde no puede ser posterior a la fecha hasta' });
      return false;
    }

    return true;
  }

  private buildSearchFilters(): Record<string, JsonApiFilter | OrFilter> {
    const filters: Record<string, JsonApiFilter | OrFilter> = {
      companyId: new Filter(FilterOperation.equals, 'companyId', '', this.filters.companyId),
      contractId: new Filter(FilterOperation.equals, 'contractId', '', this.filters.contractId),
      caseDateFrom: new Filter(FilterOperation.greaterOrEqual, 'caseDate', '', this.getStartOfDay(this.filters.dateFrom)),
      caseDateTo: new Filter(FilterOperation.lessOrEqual, 'caseDate', '', this.getEndOfDay(this.filters.dateTo)),
      caseStateIsFinal: new Filter(FilterOperation.equals, 'caseState.isFinal', '', true)
    };

    if (this.filters.name.trim()) {
      filters['clientName'] = new Filter(FilterOperation.contains, 'client.name', '', this.filters.name.trim());
    }

    if (this.filters.phone.trim()) {
      filters['clientPhone'] = new OrFilter([
        new Filter(FilterOperation.contains, 'client.phone1', '', this.filters.phone.trim()),
        new Filter(FilterOperation.contains, 'client.phone2', '', this.filters.phone.trim()),
        new Filter(FilterOperation.contains, 'client.phone3', '', this.filters.phone.trim())
      ]);
    }

    if (this.filters.email.trim()) {
      filters['clientEmail'] = new Filter(FilterOperation.contains, 'client.email', '', this.filters.email.trim());
    }

    return filters;
  }

  private async createDeleteRequest(): Promise<void> {
    this.deleting = true;
    const errors: string[] = [];
    const selectedIds = this.allMatchingCaseIds.filter(caseId => !this.deselectedCaseIds.has(caseId));

    try {
      const request: CreateDataProtectionRequestPayload = {
        reason: this.deleteRequest.reason,
        from: this.getStartOfDay(this.filters.dateFrom),
        to: this.getEndOfDay(this.filters.dateTo),
        companyId: this.filters.companyId!,
        contractId: this.filters.contractId!,
        clientName: this.filters.name.trim(),
        clientPhone: this.filters.phone.trim(),
        clientEmail: this.filters.email.trim(),
        config: {
        personalData: this.deleteRequest.personalData,
        attachments: this.deleteRequest.attachments,
        events: this.deleteRequest.events,
        retention: this.deleteRequest.retention,
        confirmPermanentDeletion: this.deleteRequest.confirmPermanentDeletion
        },
        caseIds: selectedIds
      };

      const createdRequest = await firstValueFrom(this.dataProtectionRequestsService.createRequest(request));
      this.messageService.add({ closable: true, severity: 'success', detail: `Se ha registrado la solicitud para ${createdRequest.lines} expedientes` });
    } catch (error: any) {
      errors.push(error?.error?.title || 'Ha ocurrido un error al registrar la solicitud de borrado');
    }

    this.deleting = false;

    if (errors.length > 0) {
      this.messageService.add({ closable: true, severity: 'error', detail: errors[0] });
    }
  }

  private async loadAllMatchingCaseIds(): Promise<void> {
    const params: IParamsCollection = {
      sort: ['caseDate'],
      page: { number: 1, size: this.totalRecords },
      fields: {
        cases: 'id'
      },
      filter: FilterBuilder.fromObject(this.buildSearchFilters()).build()
    };

    const response = await firstValueFrom(this.casesService.all(params));
    this.allMatchingCaseIds = response.data
      .map(caseItem => caseItem.id)
      .filter((caseId): caseId is string => !!caseId);
  }

  private syncPageSelection(): void {
    this.syncingSelection = true;
    this.selectedCases = this.cases.filter(caseItem => caseItem.id != null && !this.deselectedCaseIds.has(caseItem.id));
    this.syncingSelection = false;
  }

  private createDefaultDeleteRequest() {
    return {
      reason: '',
      confirmPermanentDeletion: false,
      personalData: {
        name: true,
        legalName: true,
        address: true,
        phone1: true,
        phone2: true,
        phone3: true,
        fax: true,
        email: true,
        vatNumber: true,
        iban: true,
        contactPerson: true,
        policy: true
      },
      attachments: {
        photos: false,
        documents: true
      },
      events: {
        calls: true
      },
      retention: {
        budgets: true,
        deliveryNotes: true,
        invoices: true
      }
    };
  }

  private getPreviousQuarterStart(): Date {
    const now = new Date();
    const currentQuarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
    return new Date(now.getFullYear(), currentQuarterStartMonth - 3, 1);
  }

  private getPreviousQuarterEnd(): Date {
    const now = new Date();
    const currentQuarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
    return new Date(now.getFullYear(), currentQuarterStartMonth, 0);
  }

  private getStartOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  }

  private getEndOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  }
}